import React from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle, ArrowRight } from "lucide-react";
import { siteConfig } from "../siteConfig";
import ServiceCard from "../components/ServiceCard";
import QuoteFormEmbed from "../components/QuoteFormEmbed";
import Reveal from "../components/Reveal";

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = siteConfig.services.find((s) => s.slug === slug);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="font-display font-black text-3xl mb-4" style={{ color: siteConfig.colors.dark }}>Service not found</h1>
          <Link to="/services" style={{ color: siteConfig.colors.primary }} className="font-semibold">← Back to Services</Link>
        </div>
      </div>
    );
  }

  const related = siteConfig.services.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      {/* Hero */}
      <section
        className="relative pt-32 pb-20"
        style={{
          backgroundImage: `url(${service.image})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0,0,0,0.65)" }} />
        <div className="relative z-10 max-w-4xl mx-auto px-5 md:px-8 text-center">
          <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: siteConfig.colors.primary }}>
            {siteConfig.businessName}
          </div>
          <h1 className="font-display font-black text-white leading-tight" style={{ fontSize: "clamp(2rem,5vw,3.5rem)" }}>
            {service.name}
          </h1>
          <p className="mt-4 text-white/75 text-lg max-w-2xl mx-auto">{service.shortDescription}</p>
        </div>
      </section>

      {/* Content + Form */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-3 gap-10">
          <Reveal as="div" className="lg:col-span-2">
            <h2 className="font-display font-black text-2xl mb-4" style={{ color: siteConfig.colors.dark }}>About This Service</h2>
            <p className="text-gray-600 leading-relaxed mb-8">{service.description}</p>

            {service.benefits?.length > 0 && (
              <>
                <h3 className="font-display font-black text-xl mb-4" style={{ color: siteConfig.colors.dark }}>Benefits</h3>
                <ul className="space-y-3 mb-8">
                  {service.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 mt-0.5 shrink-0" style={{ color: siteConfig.colors.primary }} />
                      <span className="text-gray-600">{b}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {service.commonProjects?.length > 0 && (
              <>
                <h3 className="font-display font-black text-xl mb-4" style={{ color: siteConfig.colors.dark }}>Common Projects</h3>
                <div className="grid sm:grid-cols-2 gap-3 mb-8">
                  {service.commonProjects.map((p) => (
                    <div key={p} className="flex items-center gap-2 p-3 rounded-lg" style={{ backgroundColor: siteConfig.colors.light }}>
                      <ArrowRight className="w-4 h-4" style={{ color: siteConfig.colors.primary }} />
                      <span className="text-sm font-medium" style={{ color: siteConfig.colors.dark }}>{p}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Reveal>

          <Reveal as="div" animation="right" className="lg:col-span-1">
            <div className="sticky top-24">
              <QuoteFormEmbed title={`Get a ${service.name} Quote`} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Related Services */}
      {related.length > 0 && (
        <section className="py-16" style={{ backgroundColor: siteConfig.colors.light }}>
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <Reveal>
              <h2 className="font-display font-black text-2xl mb-8" style={{ color: siteConfig.colors.dark }}>Related Services</h2>
            </Reveal>
            <div className="grid sm:grid-cols-3 gap-6">
              {related.map((s, i) => (
                <Reveal key={s.slug} delay={i * 100}>
                  <ServiceCard service={s} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

    </>
  );
};

export default ServiceDetailPage;
